import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import AuthShell from "@/pages/auth/components/AuthShell";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        navigate("/auth/forgot-password", { replace: true });
      } else {
        setChecking(false);
      }
    });
  }, [navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSuccess(true);
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <AuthShell
      title="Nouveau mot de passe"
      subtitle="Choisissez un nouveau mot de passe pour votre compte."
    >
      {success ? (
        <div className="text-center py-4">
          <i className="ri-checkbox-circle-line text-4xl text-primary-500"></i>
          <p className="mt-4 text-sm text-foreground-700">
            Votre mot de passe a bien été modifié.
          </p>
          <Link
            to="/auth/login"
            className="mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer"
          >
            Se connecter
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Nouveau mot de passe
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="6 caractères minimum"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Confirmer le mot de passe
            </label>
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Répétez le mot de passe"
              className={inputClass}
            />
          </div>

          {error && <p className="text-sm text-primary-600">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Enregistrement…
              </>
            ) : (
              "Enregistrer le mot de passe"
            )}
          </button>
        </form>
      )}
    </AuthShell>
  );
}